// #region Imports
import WorkVersionChip from "work/work-agreements/versions/_views/work-version-chip";
import ProposalPageContent from "work/proposals/_views/_shared/proposal-page-content";
import WorkVersion from "work/work-agreements/versions/work-version";
import PageTitleBreadcrumb, { breadcrumbHeadingName } from "_shared/_views/breadcrumbs/page-title-breadcrumb";
import useSessionPermissionsLoadState from "permissions/session/use-session-permissions-load-state";
import useSessionUser from "permissions/session/use-session-user";
import { proposalListPath, ProposalStageChip } from "work/proposals/_views/proposals-page-tabs";
import useSaveAsProposalTemplateAction from "work/proposal-templates/_hooks/use-save-as-proposal-template-action";
import { SAVE_AS_PROPOSAL_TEMPLATE_CAPTION } from "work/proposal-templates/save-proposal-as-template";
import CommentIcon from "@mui/icons-material/Comment";
import HistoryIcon from "@mui/icons-material/History";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ThemeProvider, styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import { HttpStatusCode } from "axios";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { ConversationPane } from "messaging/conversations/conversation-links";
import useConversationPaneRequest from "messaging/conversations/use-conversation-pane-request";
import { useSession } from "users/__session/session-context";
import { AccountType } from "users/account-types/account-type";
import { MaskedTeam } from "work/masked-fields";
import { Content, ProposalTab, TabContent, TabsContainer } from "work/_views/work-page-styled-components";
import {
  ProposalPageContainer as PageContainer,
  ProposalPageHeader as HeaderContainer,
  ProposalPageMain,
  ProposalPageTitle as PageTitle,
  ProposalPageTitleContainer as TitleContainer,
  ProposalSidePanelColumn as SidePanelColumn,
  ProposalStickyPageHeader as StickyPageHeader,
  ProposalTabsHeader,
} from "work/proposals/_views/_shared/proposal-styled-components";
import {
  PROPOSAL_SIDE_PANEL_WIDTH,
  ProposalSidePanel,
  useProposalPageCanSeatSidePanel,
} from "work/proposals/_views/_shared/proposal-page-panels";
import { paneFlushContent } from "work/_views/work-pane-layout";
import useWorkPaneTheme from "work/_views/work-pane-typography";
import useWorkPaneSize, {
  WorkPaneWidthContext,
} from "work/_views/use-work-pane-size";
import { useGetProposalByIdQuery } from "work/proposals/_api/proposal-api";
import {
  isProposalAccessDeniedError,
  PROPOSAL_ACCESS_DENIED_MESSAGE,
} from "work/proposals/_views/_shared/proposal-availability-error";
import { getServerProposalById, isProposalLoadIssue } from "work/proposals/_store/proposals-redux-slice";
import ArchivedBadge from "work/proposals/_views/archive/archived-badge";
import ConflictsTab from "work/proposals/_views/archive/tabs/archived-proposal-conflicts-tab";
import DetailsTab from "work/proposals/_views/archive/tabs/archived-proposal-details-tab";
import ClientTeamTab from "work/proposals/_views/archive/tabs/archived-proposal-client-team-tab";
import DiscountTab from "work/proposals/_views/archive/tabs/archived-proposal-discount-tab";
import FeeScheduleTab from "work/proposals/_views/archive/tabs/archived-proposal-fee-schedule-tab";
import PoliciesTab from "work/proposals/_views/archive/tabs/archived-proposal-policies-tab";
import TeamTab from "work/proposals/_views/archive/tabs/archived-proposal-team-tab";
import { PageTab } from "work/proposals/_views/drafts/draft-proposal-page-tabs";
import CommentsDrawer, { proposalCommentsTitle } from "work/proposals/comments/_view/comments-drawer";
import { useGetProposalCollaborationQuery } from "work/proposals/negotiation/proposal-collaboration-api";
import { useRevisedNegotiation } from "work/proposals/negotiation/revised-negotiation";
import {
  proposalCollaborationStorageScopeFromSession,
  proposalCollaborationViewerScope,
} from "work/proposals/negotiation/proposal-collaboration-local-store";
import { ProposalCarriedOverNegotiationContext } from "work/proposals/negotiation/proposal-carried-over-negotiation-context";
import { proposalActivityCoverage } from "work/proposals/negotiation/proposal-activity";
import ProposalActivityPopover, { proposalActivityTitle } from "work/proposals/negotiation/proposal-activity-popover";
import { ArchivedProposalNegotiationContext } from "work/proposals/_views/archive/archived-proposal-negotiation-context";
import useProposalPageAssistantScreenContext from "work/proposals/_views/_shared/use-proposal-page-assistant-screen-context";
import {
  getCommentThreadsByProposal,
  getErrorLoadingCommentThreadsByProposal,
  getIsLoadingCommentThreadsByProposal,
  populateCommentThreadsByProposal,
} from "work/proposals/comments/threads/_store/comment-thread-redux-slice";
import Proposal from "work/proposals/proposal";
import ProposalField, {
  HumanReadableProposalFieldName,
  ProposalFieldCategory,
} from "work/proposals/proposal-fields/proposal-field";
// #endregion

/**
 * The comments pane's width. It is reserved in the page's row rather than only given to the drawer:
 * a persistent Material drawer paints over the pane it sits beside, so the page used to lay its
 * content out against the full width and let the pane hide the right edge of it — three date fields
 * on Details lost 110px each on a 1280px display (#4964). Where the row cannot seat both, the pane
 * goes back to covering the content rather than squeezing it to a sliver — see
 * `useProposalPageCanSeatSidePanel`.
 */
const DRAWER_WIDTH = PROPOSAL_SIDE_PANEL_WIDTH;

// #region Styled Components
/**
 * The archived page's own padding over the shared pane. Horizontal padding is deliberately not part of
 * it: the pane declares one gutter for everything it holds (`workPaneGutters`), and adding a
 * second here would double it. Everything else — the CSS container the tabs and their fields measure
 * themselves against, and the compact treatment that container drives — comes from `ProposalPageMain`,
 * which is what the draft and active pages already use.
 */
const Main = styled(ProposalPageMain)(({ theme }) => ({
  paddingBottom: theme.spacing(3),
  paddingTop: theme.spacing(1),
}));
const HeaderButtons = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
}));
const ButtonContainer = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
}));
const MessageButton = styled(IconButton)(() => ({
  height: 40,
  width: 40,
}));
/**
 * The word under a header icon, which names the panel that button opens.
 *
 * Kept on one line: the label is wider than the 40px button above it: "Comments" is wider than 40px
 * at this size. A label that broke would take a second line
 * from the header row, which the page title shares.
 */
const MessageButtonLabel = styled(Typography)(() => ({
  fontSize: "0.6em",
  whiteSpace: "nowrap",
}));
const LoaderContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
}));
const ArchivedHeader = styled(ProposalTabsHeader)(() => ({
  paddingLeft: 0,
}));
// The fee-schedule tab used to climb out of the pane's gutter here, on the left only, with a negative
// margin and a width grown to match. Every tab's content is flush now — `paneFlushContent`, declared
// by the pane — so this had nothing left to do, and doubled up with it on the side it did cover.
// #endregion

/**
 * Each header action's caption. The icon row has no room for the full labels on a phone, so each
 * caption is one word; the button keeps its full label as its name, and its tooltip says what it does.
 */
/** The phone's one button for the header actions, named as every proposal page's collapsed actions are. */
export const ARCHIVED_MORE_ACTIONS_LABEL = "More Actions";

const ARCHIVED_ACTION_CAPTIONS: Record<string, string> = {
  "save-as-proposal-template": SAVE_AS_PROPOSAL_TEMPLATE_CAPTION,
};

export default function ArchivedProposalPage() {
  // #region Refs
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();
  const { id: idParam, pageTab: pageTabParam } = useParams();
  const navigate = useNavigate();

  // Proposal id is required
  if (!idParam) {
    throw new Response("Not Found", { status: HttpStatusCode.NotFound });
  }

  // #region State
  const [currentCommentField, setCurrentCommentField] = useState<ProposalField | undefined>();
  // The tabs below read the pane's width, not the window's — the comments panel takes a column of it.
  const { paneRef, paneWidth } = useWorkPaneSize();
  const paneTheme = useWorkPaneTheme(paneWidth);
  const canSeatSidePanel = useProposalPageCanSeatSidePanel();
  /**
   * The comments panel opens with the page — an archived proposal is a record, and the discussion is
   * most of what there is left to read on it — but only where a panel can be SEATED beside the
   * content. On a phone it cannot: the panel took the row and the tab behind it was left drawing its
   * policy list in the 100px that remained, with the proposal itself unreadable until the reader found
   * the collapse arrow. `useProposalPageCanSeatSidePanel` is the same question the page already asks
   * before reserving the panel's width. The active and draft pages start closed at every width
   * (`initialProposalPagePanelState`), so this is the only page that had to be told.
   */
  const [commentsPanelIsOpen, setCommentsPanelIsOpen] = useState<boolean>(canSeatSidePanel);
  const [commentsPanelTitle, setCommentsPanelTitle] = useState<string | undefined>();
  // #endregion

  // #region Globals
  const proposalId = useMemo(() => (idParam ? new Guid(idParam) : undefined), [idParam]);
  const activeTab = useMemo(() => {
    const tab = pageTabParam as PageTab | undefined;
    return tab ?? PageTab.Details;
  }, [pageTabParam]);

  // #region Permissions Queries
  const sessionPermissions = useSelector(getSessionPermissions);
  const permissionsLoadState = useSessionPermissionsLoadState();
  const sessionUser = useSessionUser();

  // #region Proposal Queries
  const proposalJson = useSelector(getServerProposalById(proposalId));
  const proposal = useMemo(() => {
    if (!proposalJson || isProposalLoadIssue(proposalJson) || !session.user) return null;
    return Proposal.fromJSON({
      json: proposalJson,
      currentUser: session.user,
    });
  }, [proposalJson, session.user]);

  // The secondary actions an archived proposal offers, drawn beside Comments (#6129). It has no
  // Save to put Save as Proposal Template behind, so that one stays a button of its own here.
  // Create from Previous starts from the Proposals table, not from a proposal's own page (#6260).
  const saveAsProposalTemplate = useSaveAsProposalTemplateAction({
    proposal,
    canCreateProposals: sessionUser?.canCreateProposals() ?? false,
  });
  const archivedActions = [
    ...saveAsProposalTemplate.actions,
  ];
  // On a phone, Comments and two more buttons squeeze the title down to "Proposals > ." (review of
  // #6142), so the actions that start something new share one More button there.
  const actionsInMenu = useMediaQuery((theme) => theme.breakpoints.down("sm")) && archivedActions.length > 1;
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState<HTMLElement | null>(null);

  const proposalQuery = useGetProposalByIdQuery(
    !proposalId || proposalId.isEmpty ? skipToken : { proposalId: proposalId?.value ?? Guid.EMPTY.value, session },
  );

  // #region Negotiation Queries
  // Each field's history asks for the negotiation's timeline by negotiation id, which only the envelope
  // carries. Read under the viewer's own scope — the one the active and draft pages use — so the
  // projection it brings is this reader's, and the timeline can name the items it describes from it.
  const negotiationViewerScope = useMemo(() => {
    const storageScope = proposalCollaborationStorageScopeFromSession(session);
    return storageScope ? proposalCollaborationViewerScope(storageScope) : undefined;
  }, [session]);
  const negotiationQuery = useGetProposalCollaborationQuery(
    proposalId && !proposalId.isEmpty && negotiationViewerScope
      ? { proposalId: proposalId.value, viewerScope: negotiationViewerScope }
      : skipToken,
  );
  const negotiationId = negotiationQuery.data?.negotiationId;
  const fieldHistory = useMemo(
    () => (negotiationId && negotiationViewerScope ? { negotiationId, viewerScope: negotiationViewerScope } : undefined),
    [negotiationId, negotiationViewerScope],
  );

  // An archived revision continues the negotiation it revised, as the live one did: each field's
  // history shows what came before under its own heading. The same cache scope the active page reads
  // it under, for the same reason — the earlier negotiation must not land in the store as this one.
  // Its Comments pane leads with the earlier conversation, as the live page's does (#7207).
  const {
    negotiationId: carriedOverNegotiationId,
    refused: carriedOverRefused,
    fieldHistory: carriedOverFieldHistory,
  } = useRevisedNegotiation(proposal?.revisesProposalId?.value);
  // The header's Activity popover (#5648). The archive's Team tab reads the whole team's history, and
  // its Policies tab only the viewer's own party's policies.
  const [activityAnchor, setActivityAnchor] = useState<HTMLElement | null>(null);
  const viewingAsVendor = Boolean(session.context?.viewingAsVendor);
  const activityCoverage = useMemo(
    () => proposalActivityCoverage({ wholeTeamHistory: true, policyParties: viewingAsVendor ? "vendor" : "client" }),
    [viewingAsVendor],
  );

  const openSidePanel: ProposalSidePanel | undefined = commentsPanelIsOpen ? "comments" : undefined;
  useProposalPageAssistantScreenContext({
    page: "Archived proposal",
    proposal,
    tab: activeTab,
    openPanel: openSidePanel,
    commentsTitle: commentsPanelTitle,
    action: "view",
  });

  // #region Comments Queries
  const commentThreads = useSelector(getCommentThreadsByProposal(proposal));
  const isLoadingCommentThreads = useSelector(getIsLoadingCommentThreadsByProposal(proposal));
  const wasErrorLoadingCommentThreads = useSelector(getErrorLoadingCommentThreadsByProposal(proposal));
  const isLoading = proposalQuery.isLoading || isLoadingCommentThreads;

  // A refusal is not an absence: the server answers 403 to a reader it will not show this proposal
  // to, and naming that "not found" both misdescribes it and hides the reason (#5665).
  const proposalAccessWasDenied = isProposalAccessDeniedError(proposalQuery.error);

  // #region Lifecycle
  useEffect(() => {
    // Proposal invalid/not found
    const shouldShowNotFound =
      Boolean(proposalId) && !proposalId?.isEmpty && proposal === null && !proposalQuery.isLoading;

    if (shouldShowNotFound && idParam) {
      enqueueSnackbar(proposalAccessWasDenied ? PROPOSAL_ACCESS_DENIED_MESSAGE : "Proposal not found", {
        variant: "warning",
      });
      navigate(proposalListPath(ProposalStageChip.Archived), { replace: true });
    }
  }, [activeTab, idParam, navigate, proposal, proposalAccessWasDenied, proposalId, proposalQuery.isLoading]);

  // #endregion

  // #region Functions
  function handleOpenCommentsPanel(field: ProposalField, commentsPanelTitle?: string) {
    setCommentsPanelIsOpen((prev) => (field === currentCommentField ? !prev : true));
    setCurrentCommentField(field);
    setCommentsPanelTitle(commentsPanelTitle);
  }

  // A link asked for the Comments pane: `?pane=comments`, and the conversation with `&forum=` (#5826).
  // The pane is drawn only once the threads are in the store, so the request waits for them. It opens
  // the pane over the page on a narrow window, where the pane starts closed, and selects the
  // conversation on a wide one, where the pane is open on the reader's own team. Each request opens it
  // once, so the reader can close it afterwards. An archived proposal's comments are read-only, and
  // reading them marks nothing, as opening the pane by hand does not.
  const { requestKey: commentsPaneRequestKey, forumRequest: commentsForumRequest } =
    useConversationPaneRequest(ConversationPane.Comments);
  const openedCommentsPaneRequestKeyRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!commentsPaneRequestKey || !proposal || !commentThreads) return;
    if (openedCommentsPaneRequestKeyRef.current === commentsPaneRequestKey) return;
    openedCommentsPaneRequestKeyRef.current = commentsPaneRequestKey;
    setCommentsPanelIsOpen(true);
    setCurrentCommentField(ProposalField.General);
    setCommentsPanelTitle(undefined);
  }, [commentThreads, commentsPaneRequestKey, proposal]);

  // Comment threads bootstrap
  useEffect(() => {
    if (
      !proposal ||
      proposal.supersededById ||
      isLoadingCommentThreads ||
      wasErrorLoadingCommentThreads ||
      commentThreads
    )
      return;
    dispatch(populateCommentThreadsByProposal({ proposal, session }));
  }, [proposal, isLoadingCommentThreads, wasErrorLoadingCommentThreads, commentThreads, dispatch, session]);

  // Account view switching
  /**
   * The subject this page last moved the account view for. Opening it puts the person in the party
   * they sit on, once; after that the view is theirs. Without this the effect below re-ran on every
   * new session -- including the one the account switcher publishes -- saw the "wrong" party again,
   * and switched straight back: a second PATCH undoing the first.
   */
  const accountViewAlignedFor = useRef<string | undefined | null>(null);
  useEffect(() => {
    if (!proposal || accountViewAlignedFor.current === proposalId?.value) return;
    accountViewAlignedFor.current = proposalId?.value;
    if (
      MaskedTeam.isPresentNotMasked(proposal.vendorTeam) &&
      MaskedTeam.isPresentNotMasked(proposal.clientLeader) &&
      session.context?.viewingAsVendor &&
      proposal.clientLeader?.userId.isEqualTo(session.user?.id)
    ) {
      session.setAccountViewType(AccountType.Client);
    } else if (
      MaskedTeam.isPresentNotMasked(proposal.vendorTeam) &&
      !session.context?.viewingAsVendor &&
      (proposal.vendorTeam?.leader?.userId.isEqualTo(session.user?.id) ||
        proposal.vendorTeam?.members?.some((member) => member.userId.isEqualTo(session.user?.id)))
    ) {
      session.setAccountViewType(AccountType.Vendor);
    }
  }, [proposal, proposalId, session]);

  function handleTabChange(_event: ChangeEvent<object> | null, newTab: PageTab) {
    setCommentsPanelIsOpen(false);
    setCurrentCommentField(undefined);
    setCommentsPanelTitle(undefined);
    navigate(`/proposals/archived/view/${proposalId?.value}/${newTab}`);
  }

  const handleCloseCommentsPanel = () => {
    setCommentsPanelIsOpen(false);
  };

  // "Comments", the word the live pages' pane carries (#5818). The field's name stays in front of it:
  // this page opens the pane scoped to a field.
  function getCommentsPanelTitle(): string {
    if (commentsPanelTitle) return `${commentsPanelTitle} ${proposalCommentsTitle}`;
    return `${HumanReadableProposalFieldName[currentCommentField?.name ?? ProposalField.General.name]} ${proposalCommentsTitle}`;
  }

  // #region Render
  const isFeeScheduleTab = activeTab === PageTab.FeeSchedule;

  // Permissions first, and separately from the proposal's own loading. Folding them together is what
  // made a failed permissions lookup indistinguishable from one still in flight, so the refusal below
  // was unreachable and the reader waited forever.
  if (!sessionPermissions) return permissionsLoadState;
  if (!sessionPermissions.includesAccessToProposals) return <NotAuthorized />;
  if (isLoading)
    return (
      <LoaderContainer>
        <Loader fontSize="20px" />
      </LoaderContainer>
    );

  return (
    <WorkPaneWidthContext.Provider value={paneWidth}>
    <ThemeProvider theme={paneTheme}>
    <PageContainer>
      <ProposalPageContent>
      <Main ref={paneRef} open={commentsPanelIsOpen}>
        <StickyPageHeader>
          <HeaderContainer>
            <TitleContainer>
              <PageTitle variant="h4" aria-label={breadcrumbHeadingName(["Proposals"], WorkVersion.nameWithVersion("Archived Proposal", WorkVersion.of(proposal?.workAgreement)))}>
                <PageTitleBreadcrumb label="Proposals" to="/proposals" />
                Archived Proposal
                <WorkVersionChip version={WorkVersion.of(proposal?.workAgreement)} />
              </PageTitle>
            </TitleContainer>
            <HeaderButtons>
              <ButtonContainer>
                <MessageButton
                  // The visible caption is a sibling of the button, not its content.
                  aria-label={proposalCommentsTitle}
                  size="medium"
                  color="primary"
                  disabled={!proposal?.id || !proposal || !commentThreads}
                  onClick={() => handleOpenCommentsPanel(ProposalField.General)}
                >
                  <Badge
                    variant="standard"
                    color="secondary"
                    overlap="rectangular"
                    invisible={!commentThreads?.length}
                  >
                    <CommentIcon fontSize="medium" />
                  </Badge>
                </MessageButton>
                <MessageButtonLabel
                  variant="button"
                  color={!proposal?.id || !proposal || !commentThreads ? "darkgray" : "primary"}
                >
                  {proposalCommentsTitle}
                </MessageButtonLabel>
              </ButtonContainer>
              <ButtonContainer>
                <MessageButton
                  // The visible caption is a sibling of the button, not its content.
                  aria-label={proposalActivityTitle}
                  aria-expanded={Boolean(activityAnchor)}
                  size="medium"
                  color="primary"
                  // A proposal archived before negotiations were recorded has no activity to read.
                  disabled={!negotiationId}
                  onClick={(event) => setActivityAnchor(activityAnchor ? null : event.currentTarget)}
                >
                  <HistoryIcon fontSize="medium" />
                </MessageButton>
                <MessageButtonLabel variant="button" color={negotiationId ? "primary" : "darkgray"}>
                  {proposalActivityTitle}
                </MessageButtonLabel>
              </ButtonContainer>
              <ProposalActivityPopover
                anchorEl={activityAnchor}
                onClose={() => setActivityAnchor(null)}
                negotiationId={negotiationId}
                viewerScope={negotiationViewerScope}
                coverage={activityCoverage}
                carriedOver={carriedOverFieldHistory}
              />
              {actionsInMenu && (
                <ButtonContainer>
                  <MessageButton
                    aria-label={ARCHIVED_MORE_ACTIONS_LABEL}
                    aria-haspopup="menu"
                    aria-expanded={Boolean(actionsMenuAnchor)}
                    size="medium"
                    color="primary"
                    onClick={(event) => setActionsMenuAnchor(event.currentTarget)}
                  >
                    <MoreHorizIcon fontSize="medium" />
                  </MessageButton>
                  <MessageButtonLabel variant="button" color="primary">
                    More
                  </MessageButtonLabel>
                  <Menu
                    anchorEl={actionsMenuAnchor}
                    open={Boolean(actionsMenuAnchor)}
                    onClose={() => setActionsMenuAnchor(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                  >
                    {archivedActions.map((action) => (
                      <MenuItem
                        key={action.key}
                        disabled={action.disabled || action.loading}
                        onClick={() => {
                          const anchor = actionsMenuAnchor;
                          setActionsMenuAnchor(null);
                          if (anchor instanceof HTMLButtonElement) action.onClick(anchor);
                        }}
                      >
                        <ListItemIcon>{action.icon}</ListItemIcon>
                        <ListItemText primary={action.label} />
                      </MenuItem>
                    ))}
                  </Menu>
                </ButtonContainer>
              )}
              {!actionsInMenu && archivedActions.map((action) => (
                <ButtonContainer key={action.key}>
                  <Tooltip title={action.tooltip}>
                    <span>
                      <MessageButton
                        aria-label={action.label}
                        size="medium"
                        color="primary"
                        disabled={action.disabled}
                        onClick={(event) => action.onClick(event.currentTarget)}
                      >
                        {action.loading ? <CircularProgress size={24} /> : action.icon}
                      </MessageButton>
                    </span>
                  </Tooltip>
                  <MessageButtonLabel variant="button" color={action.disabled ? "darkgray" : "primary"}>
                    {ARCHIVED_ACTION_CAPTIONS[action.key] ?? action.label}
                  </MessageButtonLabel>
                </ButtonContainer>
              ))}
              {saveAsProposalTemplate.dialog}
            </HeaderButtons>
          </HeaderContainer>
          {proposal?.frozenAt && (
            <Alert severity="info" role="status" data-testid="left-proposal-banner" sx={{ mx: 2, mt: 1 }}>
              You left this work on {proposal.frozenAt.format("MM/DD/YYYY")}. This is the proposal as it
              was then. Nothing that happened after you left is shown.
            </Alert>
          )}
          <ArchivedHeader>
            <TabsContainer
              variant="scrollable"
              scrollButtons="auto"
              indicatorColor="primary"
              textColor="primary"
              allowScrollButtonsMobile
              value={activeTab}
              onChange={handleTabChange}
            >
              <ProposalTab value={PageTab.Details} label={<ArchivedBadge category={ProposalFieldCategory.Details} />} />
              <ProposalTab value={PageTab.ClientTeam} label="Client Team" />
              <ProposalTab value={PageTab.VendorTeam} label={<ArchivedBadge title="Vendor Team" category={ProposalFieldCategory.Team} />} />
              <ProposalTab
                value={PageTab.FeeSchedule}
                label={<ArchivedBadge category={ProposalFieldCategory.FeeSchedule} />}
              />
              <ProposalTab
                value={PageTab.Conflicts}
                label={<ArchivedBadge category={ProposalFieldCategory.Conflicts} />}
              />
              <ProposalTab
                value={PageTab.Policies}
                label={<ArchivedBadge category={ProposalFieldCategory.Policies} />}
              />
              <ProposalTab
                value={PageTab.Discount}
                label={<ArchivedBadge category={ProposalFieldCategory.Discount} />}
              />
            </TabsContainer>
          </ArchivedHeader>
        </StickyPageHeader>
        <Content {...paneFlushContent}>
          {/* Each field's history reads the negotiation from here, and on a revision the one it continued. */}
          <ArchivedProposalNegotiationContext.Provider value={fieldHistory}>
          <ProposalCarriedOverNegotiationContext.Provider value={carriedOverFieldHistory}>
          <TabContent disableBottomPadding={isFeeScheduleTab}>
            {proposal && (
              <>
                {activeTab === PageTab.Details && (
                  <DetailsTab proposal={proposal} />
                )}
                {activeTab === PageTab.ClientTeam && (
                  <ClientTeamTab proposal={proposal} />
                )}
                {activeTab === PageTab.VendorTeam && (
                  <TeamTab entityId={session.user!.legalEntityId} proposal={proposal} />
                )}
                {activeTab === PageTab.FeeSchedule && (
                  <FeeScheduleTab proposal={proposal} />
                )}
                {activeTab === PageTab.Conflicts && (
                  <ConflictsTab proposal={proposal} />
                )}
                {activeTab === PageTab.Policies && (
                  <PoliciesTab proposal={proposal} />
                )}
                {activeTab === PageTab.Discount && (
                  <DiscountTab proposal={proposal} />
                )}
              </>
            )}
          </TabContent>
          </ProposalCarriedOverNegotiationContext.Provider>
          </ArchivedProposalNegotiationContext.Provider>
        </Content>
      </Main>
      </ProposalPageContent>
      {/* The pane's own column in the page row. A persistent drawer paints over its neighbour rather
          than displacing it, so without this the content laid itself out against width the pane was
          about to cover. */}
      <SidePanelColumn
        style={{ width: canSeatSidePanel && commentsPanelIsOpen && proposal && commentThreads ? DRAWER_WIDTH : 0 }}
      >
      {proposal && commentThreads && (
        <CommentsDrawer
          proposal={proposal}
          commentThreads={commentThreads}
          isOpen={commentsPanelIsOpen}
          title={getCommentsPanelTitle()}
          readOnly
          showMarkAllRead={false}
          carriedOverNegotiationId={carriedOverNegotiationId}
          carriedOverRefused={carriedOverRefused}
          forumRequest={commentsForumRequest}
          onClose={handleCloseCommentsPanel}
        />
      )}
      </SidePanelColumn>
    </PageContainer>
    </ThemeProvider>
    </WorkPaneWidthContext.Provider>
  );
}
